import React, { Fragment } from 'react';
import Yup from 'yup';
import StaticData from '../../../services/StaticData';
import Section from '../../common/Section';
import Form from '../../form';
import ProjectHeader from './ProjectHeader';
import IntervuDetails from './IntervuDetails';
import RevelationDetails from './RevelationDetails';
import DecipherDetails from './DecipherDetails';
import LiveVideoDetails from './LiveVideoDetails';
import ProjectService from '../../../services/ProjectService';
import TaskSectionList from '../Tasks/TaskSectionList';
import CommonDataService from '../../../services/CommonDataService';
import CancelButton from '../../form/controls/CancelButton';
import ContactList from '../ProjectContacts/ContactList';
import withProjectContext from '../withProjectContext';
import UserService from '../../../services/UserService';

const schema = Yup.object().shape({
  projectType: Yup.number().oneOf([0, 1, 2, 3]).required(),
  projectNumber: Yup
    .string()
    .required("Project Number required"),
  projectName: Yup
    .string()
    .required("Project Name required"),
  complexity: Yup
    .number()
    .positive()
    .integer()
    .test("selected", "Complexity is required", value => value && value > 0),
  languages: Yup
    .array()
    .min(1, "Languages must have at least 1 item"),
  startDate: Yup
    .date()
    .nullable()
    .when('projectType', {
      is: (projectType) => projectType === 1 || projectType === 2,
      then: Yup.date().required("Programming start date is required")
    }),
  launchDate: Yup
    .date()
    .nullable()
    .when('projectType', {
      is: (projectType) => projectType !== 3,
      then: Yup.date().required("Project launch date is required")
    }),
  decipher: Yup
    .object()
    .nullable()
    .when('projectType', {
      is: 1,
      then: Yup.object({
        decipherType: Yup.object().nullable().required("Decipher Type is required"),
        numberOfQuestions: Yup
          .number()
          .positive()
          .integer()
          .min(1, "Must be greater than 1")
          .required("Number of questions required")
      })
    }),
  revelation: Yup
    .object()
    .nullable()
    .when('projectType', {
      is: 2,
      then: Yup.object({
        endDate: Yup.date().required("End date is required")
      })
    }),
  liveVideo: Yup
    .object()
    .nullable()
    .when('projectType', {
      is: 3,
      then: Yup.object({
        facility: Yup.string().required("Facility name is required"),
        address: Yup.string().required("Facility address is required"),
        phone: Yup.string().required("Facility phone is required"),
        serviceType: Yup.object().nullable().required("Service Type is required"),
        videoProjectType: Yup.object().nullable().required("Video Project Type is required"),
      })
    })
})

const ProjectDetails = ({ project, ...props }) => {

  const projectType = parseInt(project.attributes.projectType, 10);

  switch (projectType) {
    case 0:
      return <IntervuDetails project={project} {...props} />;
    case 1:
      return <DecipherDetails project={project} {...props} />;
    case 2:
      return <RevelationDetails project={project} {...props} />;
    case 3:
      return <LiveVideoDetails project={project} {...props} />;
    default:
      return null;
  }
}

class ProjectForm extends React.Component {

  constructor(props) {
    super(props);

    this.state = {
      projectStatusTypes: [],
      languages: [],
      completionTypes: [],
      managers: [],
      accountTypes: [],
      message: null
    }

    this.saveChanges = this.saveChanges.bind(this);
  }

  componentDidMount() {

    CommonDataService.getProjectStatusTypes()
      .then(projectStatusTypes => {
        this.setState({ projectStatusTypes });
      });

    CommonDataService.getLanguages()
      .then(languages => {
        this.setState({ languages });
      });

    CommonDataService.getCompletionStatusTypes()
      .then(completionTypes => {
        this.setState({ completionTypes });
      });

    CommonDataService.getAccountTypes()
      .then(accountTypes => {
        this.setState({ accountTypes });
      });

    UserService
      .get({ roles: [1, 5, 6] })
      .then(users => {
        let managers = users.items.map((user) => {
          return {
            id: user.id,
            name: `${user.attributes.firstName} ${user.attributes.lastName}`
          };
        });

        this.setState({ managers });
      });
  }

  saveChanges(values) {

    const current = { ...this.props.project };
    Object.assign(current.attributes, values);

    if (current.id > 0) {
      return ProjectService.update(current);
    }

    return ProjectService.create(current);
  }

  render() {

    const project = this.props.project;
    const projectType = project.attributes.projectType;
    const liveVideoProject = projectType === 3;
    const intervuProject = projectType === 0 || liveVideoProject;
    const editMode = project.id > 0;

    return (
      <React.Fragment>
        <ProjectHeader
          projectType={projectType}
          title={this.props.title} />
        <Section className="edit-page">
          <Form
            initialValues={{ ...project.attributes }}
            enableReinitialize={false}
            validationSchema={schema}
            redirect={this.props.returnPath}
            onSubmit={this.saveChanges}>

            <Form.Errors />

            {project.id && (<h2>ID {project.id}</h2>)}

            <Form.FieldGroup label="Project number*">
              <Form.Input
                name="projectNumber"
                placeholder="Type project number" />
              {(!intervuProject && <small>(add intacct number only if it differs from project number)</small>)}
              <Form.FieldError name="projectNumber" />
            </Form.FieldGroup>

            {(!intervuProject &&
              <Form.FieldGroup label="Intacct Customer ID">
                <Form.Input
                  name="intacctCustomerId"
                  type="checkbox" />
              </Form.FieldGroup>)}

            <Form.FieldGroup label="Project name/topic*">
              <Form.Input
                name="projectName"
                placeholder="Type project name/topic" />
              <Form.FieldError name="projectName" />
            </Form.FieldGroup>

            <Form.FieldGroup label="Project status*">
              <Form.Dropdown
                name="status"
                options={this.state.projectStatusTypes} />
              <Form.FieldError name="status" />
            </Form.FieldGroup>

            <Form.FieldGroup label="Company ID*">
              <Form.Input
                name="company.sfCompanyId"
                readOnly={editMode}
                placeholder="Type company SalesForce ID" />
              <Form.FieldError name="company.sfCompanyId" />
            </Form.FieldGroup>

            <Form.FieldGroup label="Company name*">
              <Form.Input
                name="company.name"
                readOnly={editMode}
                placeholder="Type company name" />
              <Form.FieldError name="company.name" />
            </Form.FieldGroup>

            <Form.FieldGroup label="Company account type">
              <Form.Dropdown
                name="company.accountType"
                options={this.state.accountTypes}
                readOnly={editMode}
                multi={false} />
            </Form.FieldGroup>

            <Form.FieldGroup label="Contact ID*">
              <Form.Input
                name="contact.sfContactId"
                placeholder="Type contact SalesForce ID" />
              <Form.FieldError name="contact.sfContactId" />
            </Form.FieldGroup>

            <Form.FieldGroup label="Contact name*">
              <Form.Input
                name="contact.name"
                placeholder="Type contact name" />
              <Form.FieldError name="contact.name" />
            </Form.FieldGroup>

            <Form.FieldGroup label="Contact email*">
              <Form.Input
                name="contact.email"
                type="email"
                placeholder="Type contact email" />
              <Form.FieldError name="contact.email" />
            </Form.FieldGroup>

            <Form.FieldGroup label="Contact phone*">
              <Form.Input
                name="contact.phone"
                placeholder="Type contact phone" />
              <Form.FieldError name="contact.phone" />
            </Form.FieldGroup>

            <Form.FieldGroup label="Complexity*">
              <Form.RadioButton
                label="Complexity"
                name="complexity"
                options={StaticData.Complexity} />
              <Form.FieldError name="complexity" />
            </Form.FieldGroup>

            <Form.FieldGroup label="Languages*">
              <Form.Dropdown
                name="languages"
                options={this.state.languages}
                multi={true} />
              <Form.FieldError name="languages" />
            </Form.FieldGroup>

            <ProjectDetails {...this.props} />

            {editMode &&
              <Form.FieldGroup label="Tasks" className="right dates">
                <TaskSectionList
                  projectId={project.id}
                  projectType={projectType}
                  allowDelete
                  allowCreate />
              </Form.FieldGroup>}

            {editMode &&
              <Form.FieldGroup label="Contacts" className="right dates">
                <ContactList
                  projectId={project.id}
                  allowDelete
                  allowCreate />
              </Form.FieldGroup>}

            <Form.FieldGroup label="Project folder">
              <Form.Input
                name="projectFolderUrl"
                placeholder="Project folder URL" />
            </Form.FieldGroup>

            <Form.FieldGroup label="Project manager">
              <Form.Dropdown
                name="projectManager"
                options={this.state.managers} />
            </Form.FieldGroup>

            {(!intervuProject &&
              <Fragment>
                <Form.FieldGroup label="Client Requirements">
                  <Form.TextArea name="clientRequirements" />
                </Form.FieldGroup>

                <Form.FieldGroup label="SalesForce entry">
                  <Form.Input
                    name="salesForceUrl"
                    placeholder="SalesForce URL" />
                </Form.FieldGroup>

                <Form.FieldGroup label="Opportunity Id">
                  <Form.Input
                    name="opportunityId"
                    placeholder="SalesForce ID" />
                </Form.FieldGroup>

                <Form.FieldGroup label="Opportunity Owner">
                  <Form.Input
                    name="opportunityOwner"
                    placeholder="Opportunity Owner" />
                </Form.FieldGroup>

                <Form.FieldGroup label="Close Date" className="right to-right">
                  <Form.Datepicker
                    name="closeDate" />
                </Form.FieldGroup>

                <Form.FieldGroup label="Complete Target Date" className="right to-right">
                  <Form.Datepicker
                    name="completeTargetDate" />
                </Form.FieldGroup>

                <Form.FieldGroup label="Billing Target Date" className="right to-right">
                  <Form.Datepicker
                    name="billingTargetDate" />
                </Form.FieldGroup>

                <Form.FieldGroup label="Recruitment specifcations">
                  <Form.TextArea name="recruitmentSpecs" />
                </Form.FieldGroup>

                <Form.FieldGroup label="Research specifications">
                  <Form.TextArea name="researchSpecs" />
                </Form.FieldGroup>
              </Fragment>)}

            <Form.FieldGroup label="Error status*">
              <Form.Dropdown
                name="completionStatus"
                options={this.state.completionTypes} />
              <Form.FieldError name="completionStatus" />
            </Form.FieldGroup>

            {editMode &&
              <React.Fragment>
                <Form.FieldGroup label="Change in programming credits" className="right to-right">
                  <Form.Input
                    name="changeInCredits"
                    step="1"
                    type="number"
                    placeholder="0"
                    className="max-320" />
                </Form.FieldGroup>

                <Form.FieldGroup label="Change in programming cost" className="right to-right">
                  <Form.Input
                    name="changeInCost"
                    step="1"
                    type="number"
                    placeholder="0"
                    className="max-320" />
                </Form.FieldGroup>

                <Form.FieldGroup label="Change in sample cost" className="right to-right">
                  <Form.Input
                    name="changeInSampleCost"
                    step="1"
                    type="number"
                    placeholder="0"
                    className="max-320" />
                </Form.FieldGroup>
              </React.Fragment>}

            <Form.FieldGroup label="Notes">
              <Form.TextArea name="notes" />
            </Form.FieldGroup>

            <fieldset className="buttons-container">
              <button type="submit">Save</button>
              <CancelButton path={this.props.returnPath} />
            </fieldset>
          </Form>
        </Section>
      </React.Fragment>
    );
  }
}

ProjectForm.defaultProps = {
  title: "Edit Project"
}

export default withProjectContext(ProjectForm);



// WEBPACK FOOTER //
// ./src/components/projects/ProjectForm/index.js